import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthProvider';
import { toast } from "sonner";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from '@/integrations/supabase/client';

export type UserProfile = {
  user_id: string; // Primary key, references auth.users.id
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  avatar_color?: string | null;
  email?: string | null;
  created_at: string;
  updated_at: string;
  phone_number?: string | null;
  phone_verified?: boolean;
  email_verified?: boolean;
  theme_preferences?: {
    theme?: string;
    isDarkMode?: boolean;
  } | string;
  layout_preferences?: {
    theme?: string;
    sidebarColor?: string;
    topbarColor?: string;
    sidebarSize?: string;
    layoutWidth?: string;
    layoutOrientation?: string;
    twoToneIcons?: boolean;
  } | string;
  financial_preferences?: {
    defaultView?: string;
    showGainLoss?: boolean;
    showPercentages?: boolean;
    showAbsoluteValues?: boolean;
    currency?: string;
    chartType?: string;
    showMovingAverages?: boolean;
    showVolume?: boolean;
    timeframe?: string;
    groupBy?: string;
    sortBy?: string;
    showDividendYield?: boolean;
    showMarketCap?: boolean;
    enablePriceAlerts?: boolean;
    enableDividendAlerts?: boolean;
    enableEarningsAlerts?: boolean;
    enableMarketNewsAlerts?: boolean;
    compactMode?: boolean;
    showSidebar?: boolean;
    enableDarkTheme?: boolean;
    animationsEnabled?: boolean;
    autoRefresh?: boolean;
    refreshInterval?: number;
    cacheEnabled?: boolean;
  } | string;
  notification_settings?: any | string;
};

export const useUserProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [lastToastTime, setLastToastTime] = useState(0);
  const [updateTimeoutId, setUpdateTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const { data: userProfile, isLoading, error } = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('❌ [useUserProfile] Error fetching user profile:', error);
        throw error;
      }

      return data as UserProfile;
    },
    enabled: !!user,
  });

  const updateUserProfileMutation = useMutation({
    mutationFn: async (updates: Partial<UserProfile>) => {
      if (!user) throw new Error('No user logged in');

      // Prepare updates for jsonb columns WITHOUT stringifying
      const formattedUpdates: Partial<UserProfile & { updated_at: string }> = { ...updates } as any;

      // Ensure jsonb columns are objects (not JSON strings)
      const coerceJson = (val: any) => {
        if (!val) return val;
        if (typeof val === 'string') {
          try { return JSON.parse(val); } catch { return val; }
        }
        return val;
      };

      if (updates.theme_preferences !== undefined) {
        (formattedUpdates as any).theme_preferences = coerceJson(updates.theme_preferences);
      }
      if (updates.layout_preferences !== undefined) {
        (formattedUpdates as any).layout_preferences = coerceJson(updates.layout_preferences);
      }
      if (updates.financial_preferences !== undefined) {
        (formattedUpdates as any).financial_preferences = coerceJson(updates.financial_preferences);
      }
      if (updates.notification_settings !== undefined) {
        (formattedUpdates as any).notification_settings = coerceJson(updates.notification_settings);
      }

      // Add updated_at timestamp
      (formattedUpdates as any).updated_at = new Date().toISOString();

      console.log('💾 [useUserProfile] Saving to Supabase:', {
        userId: user.id,
        original: updates.financial_preferences,
        formatted: (formattedUpdates as any).financial_preferences,
        allUpdates: formattedUpdates
      });

      const { data, error } = await supabase
        .from('profiles')
        .update(formattedUpdates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('❌ [useUserProfile] Supabase error:', error);
        throw new Error(`Database update failed: ${error.message} (Code: ${error.code})`);
      }

      if (!data) {
        console.error('❌ [useUserProfile] No data returned from update');
        throw new Error('Update succeeded but no data returned - check RLS policies');
      }

      console.log('✅ [useUserProfile] Profile update successful:', data);

      // Parse all preference fields back to objects
      if (data.theme_preferences && typeof data.theme_preferences === 'string') {
        try {
          data.theme_preferences = JSON.parse(data.theme_preferences);
        } catch (e) {
          console.warn('Failed to parse theme_preferences:', data.theme_preferences);
        }
      }
      if (data.layout_preferences && typeof data.layout_preferences === 'string') {
        try {
          data.layout_preferences = JSON.parse(data.layout_preferences);
        } catch (e) {
          console.warn('Failed to parse layout_preferences:', data.layout_preferences);
        }
      }
      if (data.financial_preferences && typeof data.financial_preferences === 'string') {
        try {
          data.financial_preferences = JSON.parse(data.financial_preferences);
        } catch (e) {
          console.warn('Failed to parse financial_preferences:', data.financial_preferences);
        }
      }
      if (data.notification_settings && typeof data.notification_settings === 'string') {
        try {
          data.notification_settings = JSON.parse(data.notification_settings);
        } catch (e) {
          console.warn('Failed to parse notification_settings:', data.notification_settings);
        }
      }

      return data as UserProfile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['userProfile', user?.id], data);
      
      // Force invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['userProfile', user?.id] });
      
      // Only show toast if it's been more than 5 seconds since the last one (increased from 2s)
      const now = Date.now();
      if (now - lastToastTime > 5000) {
        toast.success('Profile updated successfully');
        setLastToastTime(now);
      }
    },
    onError: (error) => {
      console.error('❌ [useUserProfile] Error updating profile:', error);
      toast.error('Failed to update profile');
    },
  });

  // Debounced update function to prevent rapid-fire updates
  const debouncedUpdateProfile = (updates: Partial<UserProfile>) => {
    // Clear any existing timeout
    if (updateTimeoutId) {
      clearTimeout(updateTimeoutId);
    }

    // Set a new timeout to update after 300ms of no changes (reduced from 1 second)
    const newTimeoutId = setTimeout(() => {
      updateUserProfileMutation.mutate(updates);
    }, 300);

    setUpdateTimeoutId(newTimeoutId);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutId) {
        clearTimeout(updateTimeoutId);
      }
    };
  }, [updateTimeoutId]);

  return {
    profile: userProfile,
    isLoading,
    error,
    updateProfile: debouncedUpdateProfile, // Use debounced version
    updateProfileImmediate: updateUserProfileMutation.mutateAsync, // Immediate async for manual saves
    isSaving: updateUserProfileMutation.isPending
  };
};
